/**
 * DietPlanScreen.constants.js
 *
 * Copy and small option lists for the Diet Plan Screen. The plan data
 * itself (health summary, meal slots, tips) comes in as a prop — this
 * file only holds screen-level constants, not plan content.
 */

// Per FSD UC-08: risk_flag notices render on every view of the plan and
// "cannot be dismissed without acknowledgement (single 'I understand'
// button, not a silent auto-dismiss)." Acknowledgement is intentionally
// component-local (not persisted) so it really does show on every view,
// matching that requirement literally.
export const RISK_NOTICE_TEXT =
  "Based on what you've shared, we'd recommend reviewing this plan with a healthcare professional before starting it. It's still personalized to your answers, but a few factors mean extra care is worth taking.";

export const DEFAULT_DISCLAIMER =
  "This plan is generated using rule-based nutrition guidelines and AI recommendations. It is not a substitute for professional medical or dietary advice — please consult a doctor or registered dietitian for concerns specific to your health.";

export const HEALTH_SUMMARY_FIELDS = [
  { key: "calories", label: "Calories", unit: "kcal/day" },
  { key: "proteinG", label: "Protein", unit: "g/day" },
  { key: "waterL", label: "Water", unit: "L/day" },
  { key: "bmi", label: "BMI", unit: "" },
  { key: "goal", label: "Goal", unit: "" },
];

// UC-04: pre-defined per-ingredient substitution chain, filtered against
// the user's dislikes before being shown. Illustrative only — a real
// Nutrition Database would supply this per-ingredient rather than a
// single shared example chain.
export const NO_SUBSTITUTES_MESSAGE = "No safe substitutes available for this ingredient.";

// UC-03: shown when the candidate pool has nothing left to regenerate to.
export const NO_ALTERNATIVE_MESSAGE =
  "No other suitable options right now — try adjusting your preferences.";

// ── FSD v2.1 DEC-15 / Policy §15: Regeneration limits ────────────────────
// "Maximum 3 regenerations per meal and 10 total regenerations per 7-day
// plan/session. After limit, show approved alternative selection or
// support message. Limits are configurable." These are UI-side mirrors of
// the server-enforced limit — the real backend call remains the source of
// truth once UC-03 exists; this just keeps the UI from offering an action
// the API will reject, and gives the user an explanation instead of a
// silent failure.
export const MAX_REGENERATIONS_PER_MEAL = 3;
export const MAX_TOTAL_REGENERATIONS_PER_PLAN = 10;

export const REGEN_LIMIT_PER_MEAL_MESSAGE =
  "You've used all 3 regenerations for this meal. Try Replace Food on an ingredient instead, or adjust your preferences and start a new plan.";

export const REGEN_LIMIT_TOTAL_MESSAGE =
  "You've reached the regeneration limit for this plan (10 total). Try Replace Food on individual ingredients instead.";

// ── FSD v2.1 DEC-16 / Policy §15: Ingredient replacement ─────────────────
// "If daily calories move outside ±5% or protein below minimum,
// auto-rebalance the next eligible meal; if no safe rebalance exists,
// warn instead of inventing food." Shown when a replace response reports
// it could not find a safe rebalance.
export const NO_SAFE_REBALANCE_MESSAGE =
  "This swap pushed today's plan outside safe calorie/protein ranges and we couldn't automatically rebalance it. You may want to undo this swap or regenerate the meal.";

// Shown (as a courtesy, not a warning) when a replace response reports a
// successful auto-rebalance of another meal, per the same DEC-16 rule.
export const REBALANCE_APPLIED_MESSAGE =
  "Today's plan was automatically rebalanced to stay within your calorie and protein targets.";